import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/services/apiClient';
import Avatar from '@/components/ui/Avatar';
import Badge from '@/components/ui/Badge';
import Spinner from '@/components/ui/Spinner';
import { TRAINER_STATUS, GOAL_LABELS, SEX_LABELS, fmtMoney, fmtDate } from '@/lib/enums';

const Stat = ({ label, value, tone }) => (
  <div style={{ flex: 1, background: 'var(--surf2)', borderRadius: 10, padding: '10px 12px' }}>
    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--low)' }}>
      {label}
    </div>
    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, color: tone || 'var(--hi)', marginTop: 4 }}>
      {value}
    </div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--line)', fontSize: 13.5 }}>
    <span style={{ flex: 'none', width: 130, color: 'var(--low)', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', paddingTop: 2 }}>
      {label}
    </span>
    <span style={{ color: 'var(--hi)', minWidth: 0 }}>{value ?? '—'}</span>
  </div>
);

/** Detalhe de usuário/personal aberto pelo admin (clique na linha da tabela). */
export default function UserDetail({ userId }) {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setUser(null);
    setError('');
    api
      .get(`/users/${userId}`)
      .then((res) => setUser((res.data || res).user || res.data))
      .catch((e) => setError(e.message));
  }, [userId]);

  if (error) return <span style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</span>;
  if (!user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
        <Spinner />
      </div>
    );
  }

  const isTrainer = user.role === 'trainer';
  const profile = isTrainer ? user.trainerProfile : user.studentProfile;
  const trainerStatus = isTrainer ? TRAINER_STATUS[profile?.status] : null;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Avatar name={user.name} src={user.avatar} size={52} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, textTransform: 'uppercase', color: 'var(--hi)' }}>
            {user.name}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            <Badge variant="accent">{t(`roles.${user.role}`, user.role)}</Badge>
            {trainerStatus && <Badge variant={trainerStatus.variant}>{trainerStatus.label}</Badge>}
            {user.blockedAt && <Badge variant="danger">{t('users.blocked')}</Badge>}
            {user.billingBlockedAt && <Badge variant="danger">{t('userDetail.billingBlocked')}</Badge>}
          </div>
        </div>
      </div>

      {/* Contadores por papel */}
      {user.stats && (
        <div style={{ display: 'flex', gap: 10 }}>
          {isTrainer ? (
            <>
              <Stat label={t('userDetail.students')} value={user.stats.students} />
              <Stat label={t('userDetail.packages')} value={user.stats.packages} />
              <Stat label={t('userDetail.sales')} value={user.stats.sales} tone="var(--volt)" />
              <Stat
                label={t('userDetail.openInvoices')}
                value={user.stats.openInvoices}
                tone={user.stats.openInvoices > 0 ? 'var(--danger)' : undefined}
              />
            </>
          ) : (
            <>
              <Stat label={t('userDetail.purchases')} value={user.stats.purchases} />
              <Stat label={t('userDetail.completedWorkouts')} value={user.stats.completedWorkouts} tone="var(--volt)" />
            </>
          )}
        </div>
      )}

      {/* Informações */}
      <div>
        <InfoRow label={t('userDetail.email')} value={user.email} />
        <InfoRow label={t('userDetail.phone')} value={user.phone} />
        <InfoRow label={t('userDetail.plan')} value={user.plan} />
        <InfoRow label={t('userDetail.locale')} value={user.locale} />
        <InfoRow label={t('userDetail.createdAt')} value={fmtDate(user.createdAt)} />
        <InfoRow label={t('userDetail.lastLogin')} value={user.lastLoginAt ? fmtDate(user.lastLoginAt) : null} />

        {!isTrainer && (
          <>
            <InfoRow label={t('userDetail.linkedTrainer')} value={user.trainer?.name} />
            <InfoRow label={t('userDetail.goal')} value={profile?.goal ? GOAL_LABELS[profile.goal] || profile.goal : null} />
            <InfoRow label={t('userDetail.heightCm')} value={profile?.heightCm ? `${profile.heightCm} cm` : null} />
            <InfoRow label={t('userDetail.sex')} value={profile?.sex ? SEX_LABELS[profile.sex] || profile.sex : null} />
            <InfoRow
              label={t('userDetail.weeklyFrequency')}
              value={profile?.weeklyFrequency ? `${profile.weeklyFrequency}x` : null}
            />
          </>
        )}

        {isTrainer && (
          <>
            <InfoRow label={t('userDetail.city')} value={profile?.city} />
            <InfoRow
              label={t('userDetail.consultationPrice')}
              value={profile?.consultationPrice != null ? fmtMoney(profile.consultationPrice) : null}
            />
            <InfoRow
              label={t('userDetail.rating')}
              value={
                profile?.ratingCount > 0
                  ? `${Number(profile.ratingAvg).toFixed(1)} (${profile.ratingCount})`
                  : null
              }
            />
            <InfoRow
              label={t('userDetail.specialties')}
              value={
                profile?.specialties?.length ? (
                  <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {profile.specialties.map((s) => (
                      <Badge key={s} variant="accent">{s}</Badge>
                    ))}
                  </span>
                ) : null
              }
            />
            <InfoRow label={t('userDetail.bio')} value={profile?.bio} />
          </>
        )}
      </div>
    </div>
  );
}
